import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Chart as ChartJS, Tooltip, Legend } from "chart.js";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import { Chart } from "react-chartjs-2";

// Register the treemap components once, outside of component
ChartJS.register(
  TreemapController,
  TreemapElement,
  Tooltip,
  Legend
);

const imageFallback = [
  'Automotive',
  'Business',            'Community',
  'Education',           'Entertainment',
  'Finance',             'Fitness',
  'Food & Drink',        'Hotel',
  'Medical Institution', 'Rental Shop',
  'Services',            'Shop',
  'Sports & Outdoors',   'Supermarket',
  'Wellness and Beauty', 'Wellness and Health'
];

// Move getTextColor outside of component to prevent recreation
const getTextColor = (backgroundColor) => {
  // Extract RGB values assuming backgroundColor is in the format "rgb(r, g, b)" or a hex color
  let r, g, b;
  
  if (typeof backgroundColor === 'string') {
    if (backgroundColor.startsWith('#')) {
      const hex = backgroundColor.substring(1);
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (backgroundColor.startsWith('rgb')) {
      const match = backgroundColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        r = parseInt(match[1]);
        g = parseInt(match[2]);
        b = parseInt(match[3]);
      }
    }
  }
  
  // Default to black if we couldn't parse the color
  if (r === undefined || g === undefined || b === undefined) {
    return 'black';
  }
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return white for dark backgrounds, black for light backgrounds
  return luminance > 0.5 ? 'black' : 'white';
};

const createTreemapImagePlugin = () => ({
  id: 'treemapImage',
  afterDatasetDraw: (chart) => {
    const ctx = chart.ctx;
    const meta = chart.getDatasetMeta(0);
    
    // Use a flag to prevent recursion
    if (chart._isDrawingImages) return;
    chart._isDrawingImages = true;
    
    meta.data.forEach((element, index) => {
      const data = chart.data.datasets[0].tree[index];
      if (!data) return;
      
      // Get element dimensions
      const x = element.x;
      const y = element.y;
      const width = element.width;
      const height = element.height;
      
      // Positions for image and text (top left)
      const imgX = x + 15; // Image position
      const imgY = y + 15; // Top position with margin
      const textX = imgX + 15; // Text position (to the right of image)
      
      // Position for value (bottom left)
      const valueX = x + 15; // Left align with same margin
      const valueY = y + height - 15; // Bottom position with margin
      
      // Draw placeholder circle immediately
      ctx.save();
      ctx.beginPath();
      ctx.arc(imgX, imgY, 9, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = '#cccccc';
      ctx.fill();
      ctx.restore();
      
      // Function to load image without triggering redraws
      const loadImageSafely = (src, fallback) => {
        const img = new Image();
        
        img.onload = () => {
          requestAnimationFrame(() => {
            if (!chart.ctx) return;
            
            const ctx = chart.ctx;

            // Save state
            ctx.save();
            
            // Create clipping region for the image
            ctx.beginPath();
            ctx.arc(imgX, imgY, 9, 0, Math.PI * 2);
            ctx.closePath();
            ctx.clip();
            
            // Draw the image
            ctx.drawImage(img, imgX - 9, imgY - 9, 18, 18);
            
            // Restore state to draw the border
            ctx.restore();
            
            // Draw border circle (not filled, just stroked)
            ctx.beginPath();
            ctx.arc(imgX, imgY, 8, 0, Math.PI * 2);
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = .5;
            ctx.stroke();
          });
        };
        
        img.onerror = () => {
          // If image fails to load, try default
          
          if (!fallback) {
            loadImageSafely(fallback);
          }else{
            loadImageSafely('SegmentationIcons/default.png');
          }
        };
        
        img.src = src;
      };
      
      // Draw image
      if (data.Img) {
        const categoryName = data.category || '';
        const imagePath = imageFallback.includes(categoryName) ? 
                         `SegmentationIcons/${categoryName}.png` : 
                         'SegmentationIcons/default.png';
        loadImageSafely(data.Img, imagePath);
      } else {
        // Use fallback image based on category
        const categoryName = data.category || '';
        const imagePath = imageFallback.includes(categoryName) ? 
                         `SegmentationIcons/${categoryName}.png` : 
                         'SegmentationIcons/default.png';
        
        loadImageSafely(imagePath, imagePath);
      }
      
      // Helper function to truncate text with ellipsis
      const truncateText = (text, font, maxWidth) => {
        ctx.font = font;
        if (!text) return '';
        // console.log('maxWidth: ', maxWidth)
        
        let width = ctx.measureText(text).width;
        
        if (width <= maxWidth) {
          return text;
        }
        
        let truncated = text;
        const ellipsis = '...';
        const ellipsisWidth = ctx.measureText(ellipsis).width;
        
        while (truncated.length > 0) {
          truncated = truncated.slice(0, -1);
          // console.log('truncated: ', truncated)
          width = ctx.measureText(truncated).width + ellipsisWidth;
          
          if (width <= maxWidth) {
            return truncated + ellipsis;
          }
        }
        
        return ellipsis;
      };
      
      // Draw category name (or Name_final) - top left next to image
      ctx.font = '12px Arial';
      ctx.fillStyle = getTextColor(element.options.backgroundColor);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      
      // Use Name_final if available, otherwise fall back to category
      const displayName = data.Name_final || data.category || '';
      const truncatedName = truncateText(displayName, '12px Arial', width - 28);
      ctx.fillText(truncatedName, textX, imgY);
      
      // Draw value number - bottom left
      ctx.font = 'bold 24px Arial';
      ctx.fillStyle = getTextColor(element.options.backgroundColor);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      
      // Make sure value doesn't overflow
      const valueText = data.value ? data.value.toString() : '0';
      const truncatedValue = truncateText(valueText, 'bold 24px Arial', width - 30);
      
      ctx.fillText(truncatedValue, valueX, valueY);
    });
    
    // Reset the flag after all drawing is done
    chart._isDrawingImages = false;
  }
});

// Create ColorScaleLegend as a separate component
function ColorScaleLegend({ colorScale, title }) {
  const colorScaleLen = colorScale.length -1
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      marginBottom: '10px',
      width: '100%'
    }}>
      <div style={{ 
        marginBottom: 10,
        textAlign: 'left',
        width: '100%',
      }}>
        {title || 'Top Visits'}
      </div>
      <div style={{ 
        display: 'flex', 
        width: '100%', 
        justifyContent: 'stretch', 
        alignItems: 'center' 
      }}>
        {colorScale.map((scale, index) => (
          <div key={index} style={{ 
            flex: 1,
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ 
              backgroundColor: scale.color, 
              width: '100%', 
              height: '20px', 
              display: 'flex', 
              borderTopRightRadius:  index === colorScaleLen ? 5 : 0,
              borderBottomRightRadius: index === colorScaleLen ? 5 : 0,
              borderTopLeftRadius: index === 0 ? 5 : 0,
              borderBottomLeftRadius: index === 0 ? 5 : 0,
              justifyContent: 'center', 
              alignItems: 'center',
              color: getTextColor(scale.color),
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {scale.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Memoize the component after definition
const MemoizedColorScaleLegend = React.memo(ColorScaleLegend);

// Main Component
export function SegmentationPopupChartBox({ data: chartData, colorScale, title }) {
  console.log('chartData: ', chartData)
  // Create a memoized treemap plugin
  const treemapImagePlugin = useMemo(() => createTreemapImagePlugin(), []);
  
  // Memoize chart data
  const chartDataConfig = useMemo(() => ({
    datasets: [
      {
        label: "Top Retail Visits",
        tree: chartData,
        backgroundColor: (ctx) => {
          if (!ctx.raw) return '#f0f0f0';
          const value = ctx.raw.v;
          
          // Find the appropriate color from the colorScale prop
          for (const scale of colorScale) {
            const [min, max] = scale.range;
            if (value >= min && value <= max) {
              return scale.color;
            }
          }
          
          // Return the last color as default if no range matches
          return colorScale[colorScale.length - 1].color;
        },
        key: "value",
        labels: {
          display: false, // We'll use custom plugin to display labels with images
        },
        borderWidth: 1,
        borderColor: '#ffffff',
        spacing: 0, // Remove spacing between boxes
      },
    ],
  }), [chartData, colorScale]);

  // Memoize chart options
  const options = useMemo(() => ({
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false, // Turn off tooltips on hover
      }
    },
    layout: {
      padding: 0 // Remove padding
    },
    // Disable all hover effects
    hover: {
      mode: null
    },
    events: [] // Disable all events including 'mousemove', 'mouseout', 'click', etc.
  }), []);

  // Memoize color ranges for the legend
  const colorRanges = useMemo(() => 
    colorScale.map(item => ({
      color: item.color,
      label: item.label
    })), 
  [colorScale]);
  
  // Use array of plugins to avoid recreation
  const plugins = useMemo(() => [treemapImagePlugin], [treemapImagePlugin]);

  return (
    <div className="box-container p-10 mt-20">
      <MemoizedColorScaleLegend 
        colorScale={colorRanges} 
        title={title}
      />
      <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
        <Chart 
          type="treemap" 
          data={chartDataConfig} 
          options={options} 
          plugins={plugins} 
        />
      </div>
    </div>
  );
}

// Export memoized version
export const MemoizedSegmentationPopupChartBox = React.memo(SegmentationPopupChartBox);